import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { connectSocket, disconnectSocket } from '../socket';
import { registerPush } from '../push';
import { playMessageSound } from '../utils/sound';
import { setFaviconBadge } from '../utils/favicon';
import * as api from '../api/chat';

// Mô tả ngắn cho tin chỉ có đính kèm: "đã gửi 1 hình ảnh / tệp".
function attPreview(text, attachments) {
  if (text) return text;
  const atts = attachments || [];
  if (!atts.length) return '';
  const img = atts.filter((a) => (a.type || '').startsWith('image/')).length;
  const file = atts.length - img;
  if (img && !file) return `đã gửi ${img} hình ảnh`;
  if (file && !img) return `đã gửi ${file} tệp`;
  return `đã gửi ${atts.length} tệp đính kèm`;
}

const MAX_WINDOWS = 12; // trần AN TOÀN bộ nhớ; số cửa sổ HIỂN THỊ do bề ngang màn hình quyết định (không đè nhau)

const ChatContext = createContext(null);
export const useChat = () => useContext(ChatContext);

export function ChatProvider({ children }) {
  const { me } = useAuth();
  const [convs, setConvs] = useState([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('list'); // panel quản lý: 'list' | 'new' | 'settings'
  const [windows, setWindows] = useState([]); // các hội thoại đang mở (nhiều cửa sổ)
  const [onlineIds, setOnlineIds] = useState(() => new Set()); // userId đang online (chấm xanh)
  // Cài đặt thông báo (lưu localStorage).
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem('ft_chat_sound') !== '0');
  const [notifOn, setNotifOn] = useState(() => localStorage.getItem('ft_chat_notif') !== '0');
  useEffect(() => localStorage.setItem('ft_chat_sound', soundOn ? '1' : '0'), [soundOn]);
  useEffect(() => localStorage.setItem('ft_chat_notif', notifOn ? '1' : '0'), [notifOn]);
  const soundRef = useRef(soundOn);
  const notifRef = useRef(notifOn);
  useEffect(() => {
    soundRef.current = soundOn;
  }, [soundOn]);
  useEffect(() => {
    notifRef.current = notifOn;
  }, [notifOn]);

  const convsRef = useRef([]);
  const windowsRef = useRef([]);
  const openRef = useRef(false);
  useEffect(() => {
    convsRef.current = convs;
  }, [convs]);
  useEffect(() => {
    windowsRef.current = windows;
  }, [windows]);
  useEffect(() => {
    openRef.current = open;
  }, [open]);

  // Các hội thoại đang THU NHỎ (không tự đánh dấu đã đọc dù cửa sổ vẫn mở).
  const minimizedRef = useRef(new Set());
  const setConvMinimized = useCallback((convId, on) => {
    const k = String(convId);
    if (on) minimizedRef.current.add(k); else minimizedRef.current.delete(k);
  }, []);

  // Cập nhật 1 hội thoại ở CẢ danh sách lẫn cửa sổ đang mở (VD sau khi thêm/xoá thành viên nhóm).
  const patchConv = useCallback((convId, patch) => {
    const upd = (c) => (String(c.id) === String(convId) ? { ...c, ...patch } : c);
    setConvs((prev) => prev.map(upd));
    setWindows((prev) => prev.map(upd));
  }, []);

  // Bus tin nhắn: convId -> tập hợp callback của các cửa sổ đang mở.
  const subsRef = useRef(new Map());
  const subscribeMessages = useCallback((convId, fn) => {
    const k = String(convId);
    let set = subsRef.current.get(k);
    if (!set) {
      set = new Set();
      subsRef.current.set(k, set);
    }
    set.add(fn);
    return () => set.delete(fn);
  }, []);

  // Bus "đã xem": convId -> callback của cửa sổ để cập nhật dấu Đã xem.
  const readSubsRef = useRef(new Map());
  const subscribeRead = useCallback((convId, fn) => {
    const k = String(convId);
    let set = readSubsRef.current.get(k);
    if (!set) { set = new Set(); readSubsRef.current.set(k, set); }
    set.add(fn);
    return () => set.delete(fn);
  }, []);

  const totalUnread = convs.reduce((a, c) => a + (c.unread || 0), 0);
  const unreadConvs = convs.filter((c) => (c.unread || 0) > 0).length; // số cuộc hội thoại có tin mới

  // Hiện số tin mới + biểu tượng loa ngay trên tiêu đề tab trình duyệt.
  const baseTitleRef = useRef(typeof document !== 'undefined' ? document.title || 'FunnyTask' : 'FunnyTask');
  useEffect(() => {
    const base = (baseTitleRef.current || 'FunnyTask').replace(/^🔊.*?•\s*/, '');
    document.title = totalUnread > 0 ? `🔊 (${totalUnread}) Tin nhắn mới • ${base}` : base;
    setFaviconBadge(totalUnread); // chấm đỏ + số trên favicon (giống FB)
  }, [totalUnread]);

  // Popup thông báo trong app ở góc phải.
  const [toasts, setToasts] = useState([]);
  const dismissToast = (id) => setToasts((prev) => prev.filter((t) => t.id !== id));
  const pushToast = (t) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev.slice(-3), { ...t, id }]);
    setTimeout(() => dismissToast(id), 6000);
  };

  const refresh = useCallback(() => {
    api.listConversations().then(setConvs).catch(() => {});
  }, []);

  // Báo tin mới khi không mở cửa sổ đúng hội thoại: âm thanh + popup góc phải (in-app).
  // KHÔNG bắn thông báo Chrome ở đây — đã có Web Push (service worker) lo, tránh HIỆN 2 LẦN.
  const notify = (payload, conv) => {
    if (conv?.muted) return; // hội thoại đã TẮT ÂM -> không kêu, không popup (badge chưa đọc vẫn còn)
    const viewing = openRef.current && windowsRef.current.some((w) => String(w.id) === String(payload.conversation));
    if (viewing && !document.hidden) return;
    const title = conv?.type === 'group' ? conv.name : payload.sender?.name || 'Tin nhắn mới';
    const preview = attPreview(payload.text, payload.attachments);
    const body = conv?.type === 'group' ? `${payload.sender?.name}: ${preview}` : preview;
    if (soundRef.current) playMessageSound();
    pushToast({ title, body, convId: String(payload.conversation) });
  };

  // Kết nối socket + đăng ký push khi đã đăng nhập.
  useEffect(() => {
    if (!me) {
      disconnectSocket();
      setConvs([]);
      return;
    }
    refresh();
    registerPush();
    const socket = connectSocket();
    if (!socket) return;

    const onMessage = (payload) => {
      const convId = String(payload.conversation);
      const mine = String(payload.sender?._id) === String(me.id || me._id);
      const windowOpen = windowsRef.current.some((w) => String(w.id) === convId);

      // Định tuyến tới (các) cửa sổ đang mở hội thoại này.
      const set = subsRef.current.get(convId);
      if (set && set.size) set.forEach((fn) => fn(payload));
      // Đang mở cửa sổ (KHÔNG thu nhỏ) + có tin đến -> đánh dấu đã đọc.
      const viewing = openRef.current && windowOpen && !document.hidden && !minimizedRef.current.has(convId);
      if (viewing && !mine) api.markRead(convId).catch(() => {});

      // Hội thoại CHƯA có trong danh sách (VD nhóm mới vừa tạo) -> tải lại danh sách (ngoài updater để
      // React không bỏ qua). Không gọi setConvs cập nhật tại đây vì refresh() sẽ ghi đè toàn bộ.
      if (!convsRef.current.some((c) => String(c.id) === convId)) {
        refresh();
      } else {
        setConvs((prev) => {
          const idx = prev.findIndex((c) => String(c.id) === convId);
          if (idx === -1) return prev;
          const c = prev[idx];
          const viewingNow = openRef.current && windowOpen && !document.hidden && !minimizedRef.current.has(convId);
          const updated = {
            ...c,
            lastMessage: { text: attPreview(payload.text, payload.attachments), at: payload.createdAt, sender: payload.sender?._id },
            unread: mine || viewingNow ? 0 : (c.unread || 0) + 1,
          };
          return [updated, ...prev.filter((_, i) => i !== idx)];
        });
      }

      if (!mine) {
        const conv = convsRef.current.find((c) => String(c.id) === convId);
        notify(payload, conv);
      }
    };

    // Chấm xanh: nhận danh sách online lúc kết nối + cập nhật khi có người vào/ra.
    const onPresenceList = (ids) => setOnlineIds(new Set((ids || []).map(String)));
    const onPresenceUpdate = ({ userId, online }) => setOnlineIds((prev) => {
      const next = new Set(prev);
      if (online) next.add(String(userId)); else next.delete(String(userId));
      return next;
    });
    // "Đã xem": chuyển tới cửa sổ hội thoại tương ứng.
    const onRead = (evt) => {
      const set = readSubsRef.current.get(String(evt.conversation));
      if (set && set.size) set.forEach((fn) => fn(evt));
    };

    // Người gửi bấm 🔔 "nhắc lại" -> kêu + popup trong app cho người được tag (kể cả đang mở app).
    // KHÔNG tạo Notification ở đây: Web Push (service worker) đã lo thông báo hệ thống -> tránh hiện 2 lần.
    const onRemind = (e) => {
      if (soundRef.current) playMessageSound();
      pushToast({ title: e.title || '🔔 Nhắc phản hồi', body: e.body || '', convId: String(e.conversation) });
    };

    const onConversation = () => refresh();
    // Khi (re)kết nối — kể cả sau khi backend chập chờn/khởi động lại — tự đồng bộ lại
    // danh sách hội thoại (khỏi phải F5). Bỏ qua lần connect đầu (đã refresh() ở trên).
    let firstConnect = true;
    const onConnect = () => {
      if (firstConnect) { firstConnect = false; return; }
      refresh();
    };
    socket.on('connect', onConnect);
    socket.on('chat:message', onMessage);
    socket.on('chat:conversation', onConversation);
    socket.on('presence:list', onPresenceList);
    socket.on('presence:update', onPresenceUpdate);
    socket.on('chat:read', onRead);
    socket.on('chat:remind', onRemind);
    // Thông báo chung (giao việc, đề xuất, lương…): đẩy sang NotificationContext qua window event.
    const onNotify = (d) => window.dispatchEvent(new CustomEvent('ft:notify', { detail: d || {} }));
    socket.on('notify', onNotify);
    return () => {
      socket.off('connect', onConnect);
      socket.off('chat:message', onMessage);
      socket.off('chat:conversation', onConversation);
      socket.off('presence:list', onPresenceList);
      socket.off('presence:update', onPresenceUpdate);
      socket.off('chat:read', onRead);
      socket.off('chat:remind', onRemind);
      socket.off('notify', onNotify);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  // DỰ PHÒNG khi socket chập chờn: cứ ~20s làm mới danh sách hội thoại (tin cuối + số chưa đọc)
  // để sidebar tự cập nhật mà khỏi F5, kể cả khi realtime không tới.
  useEffect(() => {
    if (!me) return;
    const t = setInterval(() => { if (!document.hidden) refresh(); }, 20000);
    return () => clearInterval(t);
  }, [me, refresh]);

  // Mở 1 hội thoại thành 1 cửa sổ (nếu đã mở thì giữ nguyên). Tối đa MAX_WINDOWS.
  const openConv = useCallback((conv) => {
    setOpen(true);
    setWindows((prev) => {
      if (prev.some((w) => String(w.id) === String(conv.id))) return prev;
      return [...prev, conv].slice(-MAX_WINDOWS);
    });
    api.markRead(conv.id).catch(() => {});
    setConvs((prev) => prev.map((c) => (String(c.id) === String(conv.id) ? { ...c, unread: 0 } : c)));
  }, []);

  const closeWindow = useCallback((convId) => {
    setWindows((prev) => prev.filter((w) => String(w.id) !== String(convId)));
  }, []);

  // Mở hội thoại theo id (dùng khi bấm vào popup thông báo).
  const openConvById = useCallback(
    (id) => {
      setOpen(true);
      const c = convsRef.current.find((x) => String(x.id) === String(id));
      if (c) openConv(c);
      else refresh();
    },
    [openConv, refresh]
  );

  // Sau khi MÌNH gửi: đưa hội thoại lên đầu danh sách + cập nhật tin cuối.
  const bumpConv = useCallback((convId, msg) => {
    setConvs((prev) => {
      const idx = prev.findIndex((c) => String(c.id) === String(convId));
      if (idx === -1) return prev;
      const updated = {
        ...prev[idx],
        lastMessage: { text: attPreview(msg.text, msg.attachments), at: msg.createdAt, sender: msg.sender?._id },
      };
      return [updated, ...prev.filter((_, i) => i !== idx)];
    });
  }, []);

  const startDirect = async (userId) => {
    const conv = await api.openDirect(userId);
    setConvs((prev) => (prev.some((c) => String(c.id) === String(conv.id)) ? prev : [conv, ...prev]));
    setView('list');
    openConv(conv);
  };

  const startGroup = async (payload) => {
    const conv = await api.createGroup(payload);
    setConvs((prev) => [conv, ...prev]);
    setView('list');
    openConv(conv);
  };

  const value = {
    me,
    convs,
    totalUnread,
    unreadConvs,
    soundOn,
    setSoundOn,
    notifOn,
    setNotifOn,
    open,
    setOpen,
    view,
    setView,
    windows,
    refresh,
    openConv,
    openConvById,
    closeWindow,
    subscribeMessages,
    subscribeRead,
    setConvMinimized,
    patchConv,
    onlineIds,
    bumpConv,
    toasts,
    dismissToast,
    startDirect,
    startGroup,
  };
  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}
